import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import jsPDF from 'jspdf';

const InvoiceTable = ({
  invoices,
  onViewDetails,
  onUploadPayment,
  onUpdateStatus,
  onDelete,
    onEdit,  
  // Passed down from BillingOverview — uses each invoice's currencyCode
  formatCurrency,
}) => {
  const [expandedRow, setExpandedRow] = useState(null);

  // Fallback if parent doesn't pass formatCurrency
  const fmt = formatCurrency
    ? (value, invoice) =>
        formatCurrency(value, invoice?.currencyCode || 'EUR')
    : (value) =>
        new Intl.NumberFormat('fr-FR', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 2,
        }).format(parseFloat(value) || 0);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        label: 'Payé',
        className: 'bg-success/10 text-success',
        icon: 'CheckCircle',
      },
      pending: {
        label: 'Envoyée',
        className: 'bg-warning/10 text-warning',
        icon: 'Clock',
      },
      draft: {
        label: 'Envoyée',
      className: 'bg-warning/10 text-warning',
        icon: 'FileText',
      },
      sent: {
        label: 'Envoyé',
      className: 'bg-warning/10 text-warning',
        icon: 'Send',
      },
      partially_paid: {
        label: 'Partiel',
        className: 'bg-accent/10 text-accent',
        icon: 'PieChart',
      },
      overdue: {
        label: 'En retard',
        className: 'bg-error/10 text-error',
        icon: 'AlertCircle',
      },
      cancelled: {
        label: 'Annulé',
        className: 'bg-muted text-muted-foreground',
        icon: 'XCircle',
      },
    };

    const config = statusConfig?.[status] || statusConfig.pending;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.className}`}
      >
        <Icon name={config.icon} size={14} />
        {config.label}
      </span>
    );
  };
  const handleDownloadInvoicePdf = (invoice) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const currencyCode = invoice?.currencyCode || 'EUR';
  const W = 210;
  const margin = 14;
  const contentW = W - margin * 2;

  const fmt = (val) => {
    const formatted = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
    }).format(val ?? 0);
    return formatted.replace(/\u202f/g, ' ').replace(/\u00a0/g, ' ');
  };

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';

  const indigo = [79, 70, 229];
  const gray100 = [243, 244, 246];
  const gray400 = [156, 163, 175];
  const gray700 = [55, 65, 81];
  const green = [22, 163, 74];
  const red = [220, 38, 38];
  const white = [255, 255, 255];

  let y = margin;

  const rectFill = (x, ry, w, h, color) => {
    doc.setFillColor(...color);
    doc.rect(x, ry, w, h, 'F');
  };

  const txt = (str, x, ty, opts = {}) => {
    doc.setFontSize(opts.size || 10);
    doc.setFont('helvetica', opts.bold ? 'bold' : 'normal');
    doc.setTextColor(...(opts.color || gray700));
    doc.text(String(str ?? '—'), x, ty, { align: opts.align || 'left' });
  };

  const hline = (ly, color = gray100) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(0.3);
    doc.line(margin, ly, W - margin, ly);
  };

  // HEADER
  rectFill(0, 0, W, 32, indigo);
  doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(...white);
  doc.text('ClearMotion', margin, 14);
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(199, 210, 254);
  doc.text('Laboratoire dentaire orthodontique', margin, 20);
  doc.setFontSize(22); doc.setFont('helvetica', 'bold'); doc.setTextColor(...white);
  doc.text('FACTURE', W - margin, 13, { align: 'right' });
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(199, 210, 254);
  doc.text(invoice.invoiceNumber || '—', W - margin, 20, { align: 'right' });

  const statusLabel = invoice.status === 'paid' ? 'PAYÉE' : invoice.status === 'partially_paid' ? 'PARTIEL' : invoice.status === 'overdue' ? 'EN RETARD' : 'ENVOYÉE';
  const statusColor = invoice.status === 'paid' ? [220, 252, 231] : invoice.status === 'overdue' ? [254, 226, 226] : [219, 234, 254];
  const statusTextColor = invoice.status === 'paid' ? [22, 101, 52] : invoice.status === 'overdue' ? [153, 27, 27] : [30, 64, 175];
  doc.setFillColor(...statusColor);
  doc.roundedRect(W - margin - 30, 23, 30, 6.5, 2, 2, 'F');
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...statusTextColor);
  doc.text(statusLabel, W - margin - 15, 27.5, { align: 'center' });

  y = 40;

  // DATES ROW
  rectFill(margin, y, contentW, 14, gray100);
  const col = contentW / 3;
  [
    { label: "Date d'émission", val: fmtDate(invoice.issueDate) },
    { label: "Date d'échéance", val: fmtDate(invoice.dueDate) },
    { label: 'Date de paiement', val: fmtDate(invoice.paymentDate) },
  ].forEach((f, i) => {
    const cx = margin + i * col + col / 2;
    txt(f.label, cx, y + 5, { size: 7.5, color: gray400, align: 'center' });
    txt(f.val, cx, y + 11, { size: 8.5, bold: true, align: 'center' });
  });
  y += 20;

  // PARTIES
  const halfW = (contentW - 6) / 2;
  rectFill(margin, y, halfW, 22, gray100);
  txt('FACTURÉ À', margin + 4, y + 6, { size: 7.5, color: gray400, bold: true });
  txt(invoice.clientName || '—', margin + 4, y + 13, { size: 9, bold: true });
  txt(invoice.clientEmail || '—', margin + 4, y + 19, { size: 8, color: gray400 });

  const cx2 = margin + halfW + 6;
  rectFill(cx2, y, halfW, 22, gray100);
  txt('RÉFÉRENCE', cx2 + 4, y + 6, { size: 7.5, color: gray400, bold: true });
  txt(invoice.invoiceNumber || '—', cx2 + 4, y + 13, { size: 9, bold: true });
  txt(`Devise : ${currencyCode}`, cx2 + 4, y + 19, { size: 8, color: gray400 });
  y += 29;

  // TABLE HEADER
  rectFill(margin, y, contentW, 9, indigo);
  [
    { label: 'DESCRIPTION', x: margin + 3, align: 'left' },
    { label: 'MONTANT HT', x: W - margin - 3, align: 'right' },
  ].forEach((c) => {
    doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...white);
    doc.text(c.label, c.x, y + 6, { align: c.align });
  });
  y += 9;

  // TABLE ROW
  rectFill(margin, y, contentW, 16, [249, 250, 251]);
  hline(y, [229, 231, 235]);
  txt(`Facture ${invoice.invoiceNumber}`, margin + 3, y + 7, { size: 9, bold: true });
  txt(`Client : ${invoice.clientName || '—'}`, margin + 3, y + 13, { size: 7.5, color: gray400 });
  txt(fmt(invoice.totalAmount), W - margin - 3, y + 10, { size: 9, bold: true, align: 'right' });
  hline(y + 16, [229, 231, 235]);
  y += 24;

  // TOTALS
  const totalsX = W - margin - 62;
  const totalsLabelX = totalsX + 2;
  const totalsValX = W - margin - 2;

  const totalRows = [
    { label: 'Sous-total', val: fmt(invoice.totalAmount), color: gray700 },
    ...(parseFloat(invoice.discountAmount) > 0
      ? [{ label: 'Remise', val: `-${fmt(invoice.discountAmount)}`, color: green }]
      : []),
    ...(parseFloat(invoice.taxAmount) > 0
      ? [{ label: `TVA (${invoice.taxPercentage ?? 0}%)`, val: fmt(invoice.taxAmount), color: gray700 }]
      : []),
  ];

  totalRows.forEach((row) => {
    txt(row.label, totalsLabelX, y + 5, { size: 8, color: gray400 });
    txt(row.val, totalsValX, y + 5, { size: 8, bold: true, color: row.color, align: 'right' });
    hline(y + 7);
    y += 9;
  });

  rectFill(totalsX, y, 62, 12, indigo);
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...white);
  doc.text('TOTAL', totalsLabelX + 2, y + 8);
  doc.text(fmt(invoice.totalAmount), totalsValX - 2, y + 8, { align: 'right' });
  y += 17;

  txt('Montant payé', totalsLabelX, y + 5, { size: 8, color: gray400 });
  txt(fmt(invoice.amountPaid), totalsValX, y + 5, { size: 8, bold: true, color: green, align: 'right' });
  hline(y + 7);
  y += 9;

  const balColor = parseFloat(invoice.balanceDue) > 0 ? red : green;
  txt('Solde dû', totalsLabelX, y + 5, { size: 8.5, bold: true });
  txt(fmt(invoice.balanceDue), totalsValX, y + 5, { size: 8, bold: true, color: balColor, align: 'right' });
  y += 14;

  if (invoice.paymentMethod) {
    rectFill(margin, y, contentW, 10, gray100);
    txt('Méthode de paiement :', margin + 4, y + 7, { size: 8, color: gray400 });
    txt(invoice.paymentMethod, margin + 52, y + 7, { size: 8, bold: true });
    y += 15;
  }

  if (invoice.notes) {
    txt('Notes :', margin, y + 5, { size: 8, bold: true, color: gray400 });
    y += 8;
    const noteLines = doc.splitTextToSize(invoice.notes, contentW);
    doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(...gray700);
    doc.text(noteLines, margin, y);
  }

  // FOOTER
  rectFill(0, 285, W, 12, indigo);
  doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(199, 210, 254);
  doc.text(`Générée le ${new Date().toLocaleDateString('fr-FR')}`, margin, 292);
  doc.text(invoice.invoiceNumber || '—', W / 2, 292, { align: 'center' });
  doc.text('ClearMotion', W - margin, 292, { align: 'right' });

  doc.save(`Facture_${invoice.invoiceNumber}.pdf`);
};
  const toggleRow = (invoiceId) => {
    setExpandedRow(expandedRow === invoiceId ? null : invoiceId);
  };

  if (invoices?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <Icon name="FileText" size={32} color="var(--color-muted-foreground)" />
        </div>
        <p className="text-lg font-medium text-foreground mb-2">
          Aucune facture trouvée
        </p>
        <p className="text-sm text-muted-foreground text-center">
          Aucune facture ne correspond aux critères de recherche actuels
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-card overflow-hidden">

      {/* ── Desktop table ────────────────────────────────────────────────── */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Facture
              </th>
               <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Montant
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Payé
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Reste dû
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Détails
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Devise
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Statut
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {invoices.map((invoice) => (
              <tr
                key={invoice.id}
                className="hover:bg-muted/30 transition-colors duration-150"
              >
                {/* Invoice number */}
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {invoice.invoiceNumber}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(invoice.issueDate)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {invoice.invoiceType || 'Phase'}
                    </span>
                   
                  </div>
                </td>

                {/* Client */}
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-foreground">
                      {invoice.clientName}
                    </span>
                    {invoice.clientEmail && (
                      <span className="text-xs text-muted-foreground">
                        {invoice.clientEmail}
                      </span>
                    )}
                  </div>
                </td>

                {/* Total amount — formatted with invoice's currency */}
                <td className="px-6 py-4">
                  <span className="text-sm font-semibold text-foreground">
                    {fmt(invoice.totalAmount, invoice)}
                  </span>
                </td>

                {/* Amount paid */}
                <td className="px-6 py-4">
                  <span className="text-sm text-success font-medium">
                    {fmt(invoice.amountPaid, invoice)}
                  </span>
                </td>

                {/* Balance due */}
                <td className="px-6 py-4">
                  <span
                    className={`text-sm font-medium ${
                      parseFloat(invoice.balanceDue) > 0
                        ? 'text-error'
                        : 'text-success'
                    }`}
                  >
                    {fmt(invoice.balanceDue, invoice)}
                  </span>
                </td>

                {/* Due date */}
                <td className="px-6 py-4">
                  <span className="text-sm text-foreground">
                    {invoice.notes}
                  </span>
                </td>

                {/* Currency badge */}
                <td className="px-6 py-4">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold bg-muted text-muted-foreground">
                    {invoice.currencyCode || 'EUR'}
                  </span>
                </td>

                {/* Status */}
                <td className="px-6 py-4">{getStatusBadge(invoice.status)}</td>

                {/* Actions */}
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                   <Button variant="ghost" size="sm" onClick={() => handleDownloadInvoicePdf(invoice)} iconName="Download">
  PDF
</Button>
  {onEdit && (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onEdit(invoice)}
        iconName="FileEdit"
      >
        Modifier
      </Button>
    )}
           
                   
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Mobile card view ─────────────────────────────────────────────── */}
      <div className="lg:hidden divide-y divide-border">
        {invoices.map((invoice) => (
          <div key={invoice.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {invoice.clientName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {invoice.invoiceNumber}
                </p>
              </div>
              <button onClick={() => toggleRow(invoice.id)}>
                <Icon
                  name={
                    expandedRow === invoice.id ? 'ChevronUp' : 'ChevronDown'
                  }
                  size={20}
                  color="var(--color-muted-foreground)"
                />
              </button>
            </div>

            <div className="flex items-center justify-between mb-3">
              <span className="text-lg font-bold text-foreground">
                {fmt(invoice.totalAmount, invoice)}
              </span>
              {getStatusBadge(invoice.status)}
            </div>

            {expandedRow === invoice.id && (
              <div className="space-y-3 pt-3 border-t border-border">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Payé :</span>
                  <span className="font-medium text-success">
                    {fmt(invoice.amountPaid, invoice)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Reste dû :</span>
                  <span
                    className={`font-medium ${
                      parseFloat(invoice.balanceDue) > 0
                        ? 'text-error'
                        : 'text-success'
                    }`}
                  >
                    {fmt(invoice.balanceDue, invoice)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Échéance :</span>
                  <span className="font-medium text-foreground">
                    {formatDate(invoice.dueDate)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Devise :</span>
                  <span className="font-mono font-semibold text-foreground">
                    {invoice.currencyCode || 'EUR'}
                  </span>
                </div>
                {invoice.clientEmail && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Email :</span>
                    <span className="font-medium text-foreground">
                      {invoice.clientEmail}
                    </span>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    onClick={() => onViewDetails(invoice)}
                    iconName="Eye"
                  >
                    Détails
                  </Button>
                  {['pending', 'sent', 'partially_paid', 'overdue'].includes(
                    invoice.status
                  ) && (
                    <Button
                      variant="default"
                      size="sm"
                      fullWidth
                      onClick={() => onUploadPayment(invoice)}
                      iconName="Upload"
                    >
                      Preuve
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default InvoiceTable;